"use client";

const HEIC_RE = /\.(heic|heif)(?:$|[?#])/i;
const MIME_HEIC_RE = /image\/hei(c|f)/i;

const previewCache = new Map<string, Promise<string>>();
const convertCache = new Map<File, Promise<File>>();

function basenameWithoutExt(name: string): string {
  return name.replace(/\.[^/.]+$/, "");
}

function normalizeConvertedBlob(result: Blob | Blob[]): Blob {
  return Array.isArray(result) ? (result[0] ?? new Blob()) : result;
}

/** Проверка поддержки WebP в браузере */
let webpSupportCache: boolean | null = null;

async function supportsWebP(): Promise<boolean> {
  if (webpSupportCache !== null) return webpSupportCache;
  
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      webpSupportCache = img.width === 1 && img.height === 1;
      resolve(webpSupportCache);
    };
    img.onerror = () => {
      webpSupportCache = false;
      resolve(false);
    };
    img.src = "data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=";
  });
}

/** Оптимизированное сжатие с поддержкой WebP/JPEG */
async function compressImage(blob: Blob, maxWidth = 2048, quality = 0.85): Promise<Blob> {
  const useWebP = await supportsWebP();
  
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    
    img.onload = () => {
      let { width, height } = img;
      
      // Масштабируем только если больше maxWidth
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      if (!ctx) {
        reject(new Error("Canvas context not available"));
        return;
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      
      // WebP дает лучшее сжатие (на 25-35% меньше чем JPEG)
      const format = useWebP ? "image/webp" : "image/jpeg";
      const adjustedQuality = useWebP ? quality * 0.95 : quality; // WebP чуть выше качество
      
      canvas.toBlob(
        (result) => {
          if (result) resolve(result);
          else reject(new Error("Failed to compress image"));
        },
        format,
        adjustedQuality
      );
    };
    
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(blob);
  });
}

function toSupabaseRenderJpegUrl(raw: string): string | null {
  const [base, query] = raw.split("?");
  const marker = "/storage/v1/object/public/";
  if (!base.includes(marker)) return null;
  const convertedBase = base.replace(marker, "/storage/v1/render/image/public/");
  const qp = new URLSearchParams(query ?? "");
  qp.set("format", "jpeg");
  qp.set("quality", "80");
  /** Узкий resize снижает RAM/трафик на мобильных (HEIC на сервере → JPEG). */
  qp.set("width", "1280");
  const suffix = qp.toString();
  return suffix ? `${convertedBase}?${suffix}` : convertedBase;
}

/**
 * Порядок важен для мобильных: сначала серверный JPEG (render / соседние расширения),
 * исходный HEIC — последним, чтобы не тянуть 10+ МБ в WASM параллельно на нескольких фото.
 */
function orderedDisplayCandidates(raw: string): string[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  const [base, query] = trimmed.split("?");
  const suffix = query ? `?${query}` : "";
  const out: string[] = [];

  if (/\.(heic|heif)$/i.test(base)) {
    for (const ext of [".jpg", ".jpeg", ".webp", ".png"]) {
      out.push(base.replace(/\.(heic|heif)$/i, ext) + suffix);
    }
  }

  if (/\/cars\/cars\//i.test(base)) {
    out.push(base.replace(/\/cars\/cars\//i, "/cars/") + suffix);
  }

  const renderUrl = toSupabaseRenderJpegUrl(trimmed);
  if (renderUrl) out.push(renderUrl);

  out.push(trimmed);
  return [...new Set(out)];
}

/** Next/Image optimizer часто получает 403 на render/HEIC — грузим напрямую или через клиент. */
export function shouldBypassNextImageOptimizer(url: string): boolean {
  const u = url.trim();
  if (!u || u.startsWith("blob:")) return true;
  if (isHeicLikeUrl(u)) return true;
  if (u.includes("/render/image/")) return true;
  if (u.includes("supabase.co") && u.includes("/storage/")) return true;
  return false;
}

const DISPLAYABLE_CT_RE = /^image\/(jpeg|jpg|pjpeg|png|webp|gif)/i;

function cancelResponseBody(res: Response) {
  try {
    void res.body?.cancel();
  } catch {
    /* ignore */
  }
}

export function isHeicLikeFile(file: File): boolean {
  return MIME_HEIC_RE.test(file.type) || HEIC_RE.test(file.name);
}

export function isHeicLikeUrl(url: string): boolean {
  return HEIC_RE.test(url);
}

async function convertBlobToJpeg(blob: Blob, forUpload = false): Promise<Blob> {
  const { default: heic2any } = await import("heic2any");
  const mobile =
    typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);
  
  // Для загрузки используем более высокое качество и оптимизацию
  const quality = forUpload ? (mobile ? 0.82 : 0.90) : (mobile ? 0.72 : 0.88);
  
  const converted = await heic2any({
    blob,
    toType: "image/jpeg",
    quality,
  });
  
  const jpegBlob = normalizeConvertedBlob(converted);
  
  // Для загрузки дополнительно сжимаем через Canvas для уменьшения размера
  if (forUpload) {
    try {
      const compressed = await compressImage(jpegBlob, 2048, quality);
      // Используем сжатую версию только если она реально меньше
      return compressed.size < jpegBlob.size ? compressed : jpegBlob;
    } catch (err) {
      console.warn("Compression failed, using original:", err);
      return jpegBlob;
    }
  }
  
  return jpegBlob;
}

/** Для загрузки: конвертирует HEIC/HEIF в WebP/JPEG, остальные форматы оптимизирует. */
export async function normalizeUploadImage(file: File, addWatermark = false): Promise<File> {
  const useWebP = await supportsWebP();
  
  if (!isHeicLikeFile(file)) {
    if (!file.type.startsWith("image/")) {
      return file;
    }

    try {
      // С водяным знаком (фото машин через upload-images) — всегда canvas+лого, иначе
      // маленькие JPEG с десктопа проходят по «auto» клиентским путём без логотипа.
      if (addWatermark && typeof window !== "undefined") {
        let blob: Blob = await compressImage(file, 2048, 0.88);
        const { addWatermark: applyWatermark } = await import("./watermark");
        blob = await applyWatermark(blob);
        const ext = useWebP ? "webp" : "jpg";
        const type = useWebP ? "image/webp" : "image/jpeg";
        return new File([blob], `${basenameWithoutExt(file.name)}.${ext}`, {
          type,
          lastModified: Date.now(),
        });
      }

      // Без знака — только оптимизация крупных файлов если сжатие реально уменьшает размер
      if (file.size > 3 * 1024 * 1024) {
        let compressed = await compressImage(file, 2048, 0.88);
        if (compressed.size < file.size) {
          const ext = useWebP ? "webp" : "jpg";
          const type = useWebP ? "image/webp" : "image/jpeg";
          return new File([compressed], `${basenameWithoutExt(file.name)}.${ext}`, {
            type,
            lastModified: Date.now(),
          });
        }
      }
    } catch {
      /* compress / watermark failed — fallback below */
    }

    return file;
  }

  // Проверяем кеш, чтобы не конвертировать один файл дважды
  const cached = convertCache.get(file);
  if (cached && !addWatermark) return cached;

  const conversionPromise = (async () => {
    let convertedBlob = await convertBlobToJpeg(file, true);
    
    // Добавляем водяной знак если нужно
    if (addWatermark && typeof window !== "undefined") {
      const { addWatermark: applyWatermark } = await import("./watermark");
      convertedBlob = await applyWatermark(convertedBlob);
    }
    
    const ext = useWebP ? "webp" : "jpg";
    const type = useWebP ? "image/webp" : "image/jpeg";
    
    return new File([convertedBlob], `${basenameWithoutExt(file.name)}.${ext}`, {
      type,
      lastModified: Date.now(),
    });
  })();

  if (!addWatermark) {
    convertCache.set(file, conversionPromise);
    // Очищаем кеш через 5 минут
    setTimeout(() => convertCache.delete(file), 5 * 60 * 1000);
  }
  
  return conversionPromise;
}

/**
 * Для рендера: HEIC URL → по возможности серверный JPEG (Supabase render), иначе WASM.
 * Обычные URL возвращаются как есть.
 */
/** Синхронная подсказка: сразу URL render JPEG, без загрузки исходного HEIC в JS. */
export function getSyncDisplayUrlHint(src: string): string {
  const raw = src.trim();
  if (!raw || !isHeicLikeUrl(raw)) return raw;
  const candidates = orderedDisplayCandidates(raw);
  const preferred = candidates.find(
    (u) => !isHeicLikeUrl(u) && !u.includes("/render/image/"),
  );
  return preferred ?? raw;
}

export function getDisplayImageUrl(src: string): Promise<string> {
  const raw = src.trim();
  if (!raw || !isHeicLikeUrl(raw)) return Promise.resolve(raw);
  const candidates = orderedDisplayCandidates(raw);

  const cached = previewCache.get(raw);
  if (cached) return cached;

  const startTime = performance.now();
  
  const task = (async () => {
    let hadConversion = false;
    
    for (const url of candidates) {
      try {
        const res = await fetch(url, { cache: "force-cache" });
        if (!res.ok) continue;

        const ct = (res.headers.get("content-type") || "").trim();
        if (DISPLAYABLE_CT_RE.test(ct)) {
          cancelResponseBody(res);
          
          // Логируем производительность
          const duration = performance.now() - startTime;
          if (process.env.NODE_ENV === "development") {
            console.log(`🖼️ Image loaded in ${duration.toFixed(0)}ms (cached: ${res.headers.get("cf-cache-status") === "HIT"})`);
          }
          
          return url;
        }

        const blob = await res.blob();
        const blobType = blob.type || "";
        if (!MIME_HEIC_RE.test(blobType) && !isHeicLikeUrl(url)) {
          return URL.createObjectURL(blob);
        }
        
        hadConversion = true;
        const jpegBlob = await convertBlobToJpeg(blob);
        
        const duration = performance.now() - startTime;
        if (process.env.NODE_ENV === "development") {
          console.log(`🔄 HEIC converted in ${duration.toFixed(0)}ms`);
        }
        
        return URL.createObjectURL(jpegBlob);
      } catch {
        continue;
      }
    }
    return raw;
  })();

  previewCache.set(raw, task);
  return task;
}
